import {useCallback, useEffect, useMemo} from 'react';
import {useLocation} from 'react-router-dom';

import {ParentWindowNotifierService, authDataStore} from 'services';
import {userAuthUtils} from 'utils';

const VS_EVENT_TYPE = 'vs-app';
enum Events {
    LocationRequest = 'vsRequestLocation',
    LocationResponse = 'vsLocation',
    LocationChange = 'vsLocationChange',
    UserAuthorized = 'vsUserAuthorized',
    UserNotAuthorized = 'vsUserNotAuthorized',
    UserLogout = 'vsUserLogout',
}

export const AppNotifier: React.FC = () => {
    const {pathname} = useLocation();

    const locationData = useMemo(() => {
        const {href, host, origin, port, protocol} = window.location;
        return {href, host, origin, pathname, port, protocol};
    }, [pathname]);

    const notify = useCallback((event: {name: Events, data?: any}) => {
        ParentWindowNotifierService.notify({
            type: VS_EVENT_TYPE,
            event
        });
    }, []);

    const onMessage = useCallback((e: MessageEvent) => {
        if (e.data.type !== VS_EVENT_TYPE) {
            return;
        }
        const {event: {name}} = e.data;

        if (name === Events.LocationRequest) {
            notify({
                name: Events.LocationResponse,
                data: {
                    location: locationData
                }
            });
        }
    }, [notify, locationData]);

    useEffect(() => {
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
        };
    }, [onMessage]);

    useEffect(() => {
        notify({
            name: Events.LocationChange,
            data: {
                location: locationData
            }
        });
    }, [notify, locationData]);

    useEffect(() => authDataStore.subscribe((value, prevValue) => {
        if (value?.localUser && !prevValue?.localUser) {
            notify({name: Events.UserAuthorized});
        }
        if (value?.logoutType && value.logoutType !== prevValue?.logoutType) {
            notify({name: Events.UserLogout, data: {logoutType: value.logoutType}});
        }
        if (!value?.localUser && prevValue?.localUser) {
            notify({name: Events.UserNotAuthorized});
        }
    }), [notify]);

    useEffect(() => {
        if (!userAuthUtils.isLoggedIn()) {
            notify({name: Events.UserNotAuthorized});
        }
    }, [notify]);

    return null;
};